import { FC } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Logo } from '../logo/logo';

export const SplashScreenLayout: FC = (): JSX.Element => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const primary = theme.palette.primary.main;

    return (
        <Box
            role="status"
            aria-live="polite"
            aria-label="در حال بارگذاری"
            sx={{
                width: '100vw',
                height: '100dvh',
                minHeight: '100svh',
                position: 'fixed',
                inset: 0,
                zIndex: 1400,
                overflow: 'hidden',
                display: 'grid',
                placeItems: 'center',
                boxSizing: 'border-box',
                padding: 'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
                backgroundColor: 'background.default',
                '@keyframes loaderSpin': {
                    to: { transform: 'rotate(360deg)' },
                },
                '@keyframes loaderSpinReverse': {
                    to: { transform: 'rotate(-360deg)' },
                },
                '@keyframes loaderPulse': {
                    '0%, 100%': { transform: 'scale(0.96)', opacity: 0.5 },
                    '50%': { transform: 'scale(1.04)', opacity: 0.92 },
                },
                '@keyframes loaderCore': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.035)' },
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: 'clamp(220px, 58vmin, 430px)',
                    height: 'clamp(220px, 58vmin, 430px)',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(
                        primary,
                        isDark ? 0.13 : 0.07,
                    )} 0%, ${alpha(primary, 0.025)} 40%, transparent 72%)`,
                    filter: 'blur(clamp(7px, 1.8vmin, 12px))',
                    animation: 'loaderPulse 2.8s ease-in-out infinite',
                    pointerEvents: 'none',
                },
                '@media (prefers-reduced-motion: reduce)': {
                    '& *, &::before': {
                        animationDuration: '8s !important',
                    },
                },
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    position: 'relative',
                    width: 'clamp(96px, 25vmin, 136px)',
                    aspectRatio: '1 / 1',
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        border: '1px solid',
                        borderColor: alpha(primary, isDark ? 0.2 : 0.13),
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: -1,
                            borderRadius: '50%',
                            border: '2px solid transparent',
                            borderTopColor: alpha(primary, 0.96),
                            borderInlineEndColor: alpha(primary, 0.34),
                            animation: 'loaderSpin 1.05s linear infinite',
                        },
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        inset: '9%',
                        borderRadius: '50%',
                        border: '1px dashed',
                        borderColor: alpha(primary, isDark ? 0.27 : 0.18),
                        animation: 'loaderSpinReverse 5.5s linear infinite',
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        inset: '-5%',
                        borderRadius: '50%',
                        animation: 'loaderSpin 2.25s linear infinite',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '2%',
                            left: '50%',
                            width: 'clamp(5px, 1.4vmin, 7px)',
                            height: 'clamp(5px, 1.4vmin, 7px)',
                            borderRadius: '50%',
                            backgroundColor: primary,
                            boxShadow: `0 0 14px ${alpha(primary, 0.72)}`,
                            transform: 'translateX(-50%)',
                        },
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        inset: '4%',
                        borderRadius: '50%',
                        animation: 'loaderSpinReverse 3s linear infinite',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: '1%',
                            left: '50%',
                            width: 'clamp(3px, 0.9vmin, 5px)',
                            height: 'clamp(3px, 0.9vmin, 5px)',
                            borderRadius: '50%',
                            backgroundColor: alpha(primary, 0.58),
                            transform: 'translateX(-50%)',
                        },
                    }}
                />

                <Box
                    sx={{
                        width: '53%',
                        aspectRatio: '1 / 1',
                        position: 'relative',
                        zIndex: 2,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '32%',
                        backgroundColor: alpha(
                            theme.palette.background.paper,
                            isDark ? 0.95 : 0.985,
                        ),
                        border: '1px solid',
                        borderColor: alpha(primary, isDark ? 0.24 : 0.14),
                        boxShadow: `0 14px 44px ${alpha(
                            primary,
                            isDark ? 0.17 : 0.1,
                        )}, inset 0 1px 0 ${alpha('#ffffff', isDark ? 0.05 : 0.75)}`,
                        animation: 'loaderCore 2.2s ease-in-out infinite',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: '8%',
                            borderRadius: '27%',
                            border: '1px solid',
                            borderColor: alpha(primary, isDark ? 0.09 : 0.055),
                            pointerEvents: 'none',
                        },
                        '& > *': {
                            transform: { xs: 'scale(0.88)', sm: 'scale(1)' },
                            transformOrigin: 'center',
                        },
                    }}
                >
                    <Logo size="small" />
                </Box>
            </Box>
        </Box>
    );
};
